import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Wrench, X, ZoomIn, ChevronLeft as ChevronLeftIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

// Lightbox Component
const Lightbox = ({ image, onClose }) => {
  if (!image) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
        onClick={onClose}
      >
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
          onClick={onClose}
        >
          <X size={24} className="text-white" />
        </motion.button>
        
        <motion.img
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          src={image}
          alt="Enlarged view"
          className="max-w-[90vw] max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Image Carousel Component
const ImageCarousel = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="card-technical p-12 text-center h-80 flex items-center justify-center">
        <p className="text-[#94A3B8] text-lg">Images coming soon...</p>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative">
      <div className="relative overflow-hidden border border-[#6B8CAE]/30 bg-white/5 h-80">
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="w-full h-full object-contain cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        />
        
        <div 
          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20 cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        >
          <div className="w-12 h-12 bg-[#6B8CAE] flex items-center justify-center">
            <ZoomIn size={24} className="text-white" />
          </div>
        </div>

        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronLeftIcon size={20} className="text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronRight size={20} className="text-white" />
            </button>
          </>
        )}

        <div className="absolute bottom-3 right-3 bg-[#0F172A]/80 px-3 py-1 text-white text-sm font-mono">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-20 h-16 border-2 overflow-hidden transition-all ${
                index === currentIndex 
                  ? 'border-[#6B8CAE] opacity-100' 
                  : 'border-transparent opacity-50 hover:opacity-75'
              }`}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PrototypingPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [lightboxImage, setLightboxImage] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const texts = {
    en: {
      back: 'Back to Services',
      title: 'Prototyping',
      description: 'Prototyping is a critical step between design and production. It allows product development teams and industrial companies to validate concepts, test functionality and identify design improvements before committing to serial manufacturing. By choosing the right prototyping method for the specific geometry, material and application, development becomes faster, more controlled and more cost-effective.',
      clickToZoom: 'Click image to enlarge',
      servicesTitle: 'Our Services',
      items: [
        'CNC parts for prototypes',
        'Sheet metal parts for prototypes',
        '3D printing with FDM and SLS',
        'Silicone molded parts'
      ]
    },
    bg: {
      back: 'Обратно към Услуги',
      title: 'Прототипиране',
      description: 'Прототипирането е критична стъпка между дизайна и производството. То позволява на екипите за разработка на продукти и индустриалните компании да валидират концепции, тестват функционалност и идентифицират подобрения в дизайна преди да се ангажират със серийно производство. Като изберете правилния метод за прототипиране за конкретната геометрия, материал и приложение, разработката става по-бърза, по-контролирана и по-рентабилна.',
      clickToZoom: 'Кликнете върху изображението за увеличаване',
      servicesTitle: 'Нашите услуги',
      items: [
        'CNC части за прототипи',
        'Ламаринени части за прототипи',
        '3D печат с FDM и SLS',
        'Силиконови отливки'
      ]
    }
  };

  const t = texts[language] || texts.en;

  // Prototyping images - CNC and 3D printing
  const images = [
    'https://images.unsplash.com/photo-1625465104346-1ec72f86a54b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxDTkMlMjBtYWNoaW5pbmclMjBtZXRhbCUyMHByb3RvdHlwZSUyMG1hbnVmYWN0dXJpbmd8ZW58MHx8fHwxNzc1OTA2MzUxfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1624841969713-d7c371f2da25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxDTkMlMjBtYWNoaW5pbmclMjBtZXRhbCUyMHByb3RvdHlwZSUyMG1hbnVmYWN0dXJpbmd8ZW58MHx8fHwxNzc1OTA2MzUxfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1769151174211-a1b2d41117bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHwzRCUyMHByaW50aW5nJTIwcHJvdG90eXBlJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzU5MDYzNTN8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1762579318096-8e9bc45b8feb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHwzRCUyMHByaW50aW5nJTIwcHJvdG90eXBlJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzU5MDYzNTN8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1624775101932-6a3768eac108?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxDTkMlMjBtYWNoaW5pbmclMjBtZXRhbCUyMHByb3RvdHlwZSUyMG1hbnVmYWN0dXJpbmd8ZW58MHx8fHwxNzc1OTA2MzUxfDA&ixlib=rb-4.1.0&q=85'
  ];

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <Navbar />
      
      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#6B8CAE] hover:text-white transition-colors mb-8"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm uppercase tracking-wider">{t.back}</span>
          </motion.button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center">
                <Wrench size={32} className="text-[#6B8CAE]" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white" data-testid="prototyping-page-title">
                {t.title}
              </h1>
            </div>
            <p className="text-[#94A3B8] text-lg leading-relaxed max-w-4xl">
              {t.description}
            </p>
          </motion.div>

          {/* Content Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid lg:grid-cols-2 gap-12"
          >
            {/* Items List */}
            <div className="card-technical p-8">
              <h2 className="text-2xl font-semibold text-white mb-8 flex items-center space-x-3">
                <Wrench size={24} className="text-[#6B8CAE]" />
                <span>{t.servicesTitle}</span>
              </h2>
              <ul className="space-y-4">
                {t.items.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start space-x-3 text-[#94A3B8]"
                  >
                    <ChevronRight size={20} className="text-[#6B8CAE] flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Images Carousel */}
            <div>
              <ImageCarousel 
                images={images} 
                onImageClick={(img) => setLightboxImage(img)}
              />
              {images.length > 0 && (
                <p className="text-[#6B8CAE] text-sm mt-3 text-center font-mono">
                  <ZoomIn size={14} className="inline mr-1" />
                  {t.clickToZoom}
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrototypingPage;
